import { Box, Typography } from '@mui/material';
import ShieldOutlinedIcon from '@mui/icons-material/ShieldOutlined';
import CurrencyRupeeIcon from '@mui/icons-material/CurrencyRupee';
import SupportAgentOutlinedIcon from '@mui/icons-material/SupportAgentOutlined';
import EventAvailableOutlinedIcon from '@mui/icons-material/EventAvailableOutlined';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';

export function ConnectedCommunityIllustration() {

  return (
    <Box
      sx={{
        width: '100%',
        maxWidth: 520,
        mx: 'auto',
        my: 'auto',
        position: 'relative',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        userSelect: 'none',
        py: 2,
        '@media (prefers-reduced-motion: reduce)': {
          '& *': {
            animation: 'none !important',
            transition: 'none !important',
          },
        },
        '@keyframes floatSlow': {
          '0%, 100%': { transform: 'translateY(0px)' },
          '50%': { transform: 'translateY(-6px)' },
        },
        '@keyframes floatReverse': {
          '0%, 100%': { transform: 'translateY(0px)' },
          '50%': { transform: 'translateY(6px)' },
        },
        '@keyframes pulseGlow': {
          '0%, 100%': { opacity: 0.35, transform: 'scale(1)' },
          '50%': { opacity: 0.7, transform: 'scale(1.04)' },
        },
        '@keyframes pulseRing': {
          '0%': { transform: 'scale(0.9)', opacity: 0.8 },
          '50%': { transform: 'scale(1.1)', opacity: 0.3 },
          '100%': { transform: 'scale(0.9)', opacity: 0.8 },
        },
      }}
    >
      {/* Background Soft Glows */}
      <Box
        sx={{
          position: 'absolute',
          width: 320,
          height: 320,
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(0,184,148,0.22) 0%, rgba(30,58,138,0.1) 60%, transparent 80%)',
          filter: 'blur(30px)',
          zIndex: 0,
          animation: 'pulseGlow 6s ease-in-out infinite',
        }}
      />

      {/* SVG Canvas for Society Architecture & Connected Network Grid */}
      <Box
        component="svg"
        viewBox="0 0 500 360"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
        sx={{
          width: '100%',
          height: 'auto',
          maxHeight: 340,
          zIndex: 1,
          filter: 'drop-shadow(0 10px 25px rgba(0,0,0,0.3))',
        }}
      >
        <defs>
          {/* Gradients */}
          <linearGradient id="towerGrad1" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#1E293B" stopOpacity="0.85" />
            <stop offset="100%" stopColor="#0F172A" stopOpacity="0.95" />
          </linearGradient>
          <linearGradient id="towerGrad2" x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor="#00B894" stopOpacity="0.3" />
            <stop offset="100%" stopColor="#1E3A8A" stopOpacity="0.8" />
          </linearGradient>
          <linearGradient id="accentGrad" x1="0%" y1="0%" x2="100%" y2="100%">
            <stop offset="0%" stopColor="#00B894" />
            <stop offset="100%" stopColor="#008F78" />
          </linearGradient>
          <linearGradient id="lineGlow" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#00B894" stopOpacity="0.1" />
            <stop offset="50%" stopColor="#00B894" stopOpacity="0.8" />
            <stop offset="100%" stopColor="#38BDF8" stopOpacity="0.1" />
          </linearGradient>
          <filter id="glow">
            <feGaussianBlur stdDeviation="3" result="coloredBlur" />
            <feMerge>
              <feMergeNode in="coloredBlur" />
              <feMergeNode in="SourceGraphic" />
            </feMerge>
          </filter>
        </defs>

        {/* Outer Connection Network Arcs */}
        <path
          d="M 80 200 C 150 120, 350 120, 420 200"
          stroke="url(#lineGlow)"
          strokeWidth="2"
          strokeDasharray="4 6"
          opacity="0.6"
        />
        <path
          d="M 120 240 C 200 170, 300 170, 380 240"
          stroke="url(#lineGlow)"
          strokeWidth="1.5"
          opacity="0.5"
        />
        <path
          d="M 250 80 L 250 260"
          stroke="url(#lineGlow)"
          strokeWidth="1.5"
          strokeDasharray="6 6"
          opacity="0.4"
        />

        {/* Foundation Platform */}
        <ellipse cx="250" cy="275" rx="210" ry="42" fill="#0B132B" opacity="0.7" stroke="#1E293B" strokeWidth="1.5" />
        <ellipse cx="250" cy="275" rx="160" ry="30" fill="url(#lineGlow)" opacity="0.15" />

        {/* Left Residential Tower */}
        <g opacity="0.9">
          <rect x="140" y="130" width="65" height="135" rx="6" fill="url(#towerGrad1)" stroke="#334155" strokeWidth="1.5" />
          {/* Windows Grid */}
          <rect x="150" y="145" width="10" height="12" rx="2" fill="#F8FAFC" opacity="0.7" />
          <rect x="168" y="145" width="10" height="12" rx="2" fill="#00B894" opacity="0.9" filter="url(#glow)" />
          <rect x="186" y="145" width="10" height="12" rx="2" fill="#F8FAFC" opacity="0.6" />

          <rect x="150" y="168" width="10" height="12" rx="2" fill="#00B894" opacity="0.8" />
          <rect x="168" y="168" width="10" height="12" rx="2" fill="#F8FAFC" opacity="0.7" />
          <rect x="186" y="168" width="10" height="12" rx="2" fill="#38BDF8" opacity="0.8" />

          <rect x="150" y="191" width="10" height="12" rx="2" fill="#F8FAFC" opacity="0.5" />
          <rect x="168" y="191" width="10" height="12" rx="2" fill="#F8FAFC" opacity="0.8" />
          <rect x="186" y="191" width="10" height="12" rx="2" fill="#00B894" opacity="0.9" />

          <rect x="150" y="214" width="10" height="12" rx="2" fill="#F8FAFC" opacity="0.8" />
          <rect x="168" y="214" width="10" height="12" rx="2" fill="#38BDF8" opacity="0.7" />
          <rect x="186" y="214" width="10" height="12" rx="2" fill="#F8FAFC" opacity="0.6" />
        </g>

        {/* Right Residential Tower */}
        <g opacity="0.9">
          <rect x="295" y="130" width="65" height="135" rx="6" fill="url(#towerGrad1)" stroke="#334155" strokeWidth="1.5" />
          {/* Windows Grid */}
          <rect x="305" y="145" width="10" height="12" rx="2" fill="#00B894" opacity="0.9" />
          <rect x="323" y="145" width="10" height="12" rx="2" fill="#F8FAFC" opacity="0.7" />
          <rect x="341" y="145" width="10" height="12" rx="2" fill="#F8FAFC" opacity="0.5" />

          <rect x="305" y="168" width="10" height="12" rx="2" fill="#F8FAFC" opacity="0.6" />
          <rect x="323" y="168" width="10" height="12" rx="2" fill="#38BDF8" opacity="0.8" />
          <rect x="341" y="168" width="10" height="12" rx="2" fill="#00B894" opacity="0.9" />

          <rect x="305" y="191" width="10" height="12" rx="2" fill="#00B894" opacity="0.8" />
          <rect x="323" y="191" width="10" height="12" rx="2" fill="#F8FAFC" opacity="0.7" />
          <rect x="341" y="191" width="10" height="12" rx="2" fill="#F8FAFC" opacity="0.8" />

          <rect x="305" y="214" width="10" height="12" rx="2" fill="#F8FAFC" opacity="0.7" />
          <rect x="323" y="214" width="10" height="12" rx="2" fill="#00B894" opacity="0.9" />
          <rect x="341" y="214" width="10" height="12" rx="2" fill="#38BDF8" opacity="0.7" />
        </g>

        {/* Center Iconic Community Clubhouse / Hub */}
        <g>
          <rect x="212" y="95" width="76" height="170" rx="8" fill="url(#towerGrad2)" stroke="#00B894" strokeWidth="2" />
          {/* Modern Architectural Spires */}
          <line x1="250" y1="95" x2="250" y2="70" stroke="#00B894" strokeWidth="2.5" strokeLinecap="round" />
          <circle cx="250" cy="68" r="4" fill="#00B894" filter="url(#glow)" />

          {/* Central Logo / Pulse Emblem */}
          <circle cx="250" cy="140" r="18" fill="#14213D" stroke="#00B894" strokeWidth="1.5" />
          <path d="M 243 135 L 250 148 L 257 135" stroke="#00B894" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />

          {/* Hub Glass Facade */}
          <rect x="222" y="172" width="56" height="30" rx="4" fill="#00B894" fillOpacity="0.2" stroke="#00B894" strokeWidth="1" />
          <rect x="222" y="210" width="56" height="42" rx="4" fill="#38BDF8" fillOpacity="0.15" stroke="#38BDF8" strokeWidth="1" />
        </g>

        {/* Security Gate & Access Control Point */}
        <g transform="translate(215, 252)">
          <rect x="0" y="0" width="70" height="24" rx="4" fill="#0F172A" stroke="#334155" strokeWidth="1.5" />
          <line x1="10" y1="12" x2="60" y2="12" stroke="#00B894" strokeWidth="2" strokeLinecap="round" />
          <circle cx="20" cy="12" r="3" fill="#22C55E" />
          <circle cx="50" cy="12" r="3" fill="#22C55E" />
        </g>

        {/* Pulsing Connected Nodes */}
        <circle cx="100" cy="120" r="5" fill="#00B894" filter="url(#glow)" />
        <circle cx="400" cy="120" r="5" fill="#38BDF8" filter="url(#glow)" />
        <circle cx="70" cy="220" r="4" fill="#F59E0B" />
        <circle cx="430" cy="220" r="4" fill="#A855F7" />
      </Box>

      {/* Floating Micro Feature Concept Badges */}
      {/* 1. Gate Pass Visitor Badge (Top Left) */}
      <Box
        sx={{
          position: 'absolute',
          top: '6%',
          left: '4%',
          bgcolor: 'rgba(15, 23, 42, 0.88)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(0, 184, 148, 0.4)',
          borderRadius: '12px',
          px: 1.5,
          py: 0.85,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          boxShadow: '0 8px 24px rgba(0, 184, 148, 0.2)',
          zIndex: 2,
          animation: 'floatSlow 5s ease-in-out infinite',
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '8px',
            bgcolor: 'rgba(0, 184, 148, 0.18)',
            color: '#00B894',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ShieldOutlinedIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#F8FAFC', lineHeight: 1.2 }}>
            Gate Pass #GP-2026
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', color: '#00B894', fontWeight: 650, display: 'flex', alignItems: 'center', gap: 0.3 }}>
            <CheckCircleRoundedIcon sx={{ fontSize: 11 }} /> Pre-Approved
          </Typography>
        </Box>
      </Box>

      {/* 2. Dues Payment Badge (Top Right) */}
      <Box
        sx={{
          position: 'absolute',
          top: '12%',
          right: '4%',
          bgcolor: 'rgba(15, 23, 42, 0.88)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(56, 189, 248, 0.4)',
          borderRadius: '12px',
          px: 1.5,
          py: 0.85,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          boxShadow: '0 8px 24px rgba(56, 189, 248, 0.2)',
          zIndex: 2,
          animation: 'floatReverse 6s ease-in-out infinite',
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '8px',
            bgcolor: 'rgba(56, 189, 248, 0.18)',
            color: '#38BDF8',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <CurrencyRupeeIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#F8FAFC', lineHeight: 1.2 }}>
            Maintenance Dues
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', color: '#38BDF8', fontWeight: 650, display: 'flex', alignItems: 'center', gap: 0.3 }}>
            <CheckCircleRoundedIcon sx={{ fontSize: 11 }} /> Paid Instantly
          </Typography>
        </Box>
      </Box>

      {/* 3. Complaint Resolved Badge (Bottom Left) */}
      <Box
        sx={{
          position: 'absolute',
          bottom: '8%',
          left: '6%',
          bgcolor: 'rgba(15, 23, 42, 0.88)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(168, 85, 247, 0.4)',
          borderRadius: '12px',
          px: 1.5,
          py: 0.85,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          boxShadow: '0 8px 24px rgba(168, 85, 247, 0.2)',
          zIndex: 2,
          animation: 'floatSlow 7s ease-in-out infinite 1s',
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '8px',
            bgcolor: 'rgba(168, 85, 247, 0.18)',
            color: '#A855F7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <SupportAgentOutlinedIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#F8FAFC', lineHeight: 1.2 }}>
            Elevator Maintenance
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', color: '#A855F7', fontWeight: 650 }}>
            Ticket Resolved · 10m
          </Typography>
        </Box>
      </Box>

      {/* 4. Community Events Badge (Bottom Right) */}
      <Box
        sx={{
          position: 'absolute',
          bottom: '10%',
          right: '6%',
          bgcolor: 'rgba(15, 23, 42, 0.88)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(245, 158, 11, 0.4)',
          borderRadius: '12px',
          px: 1.5,
          py: 0.85,
          display: 'flex',
          alignItems: 'center',
          gap: 1,
          boxShadow: '0 8px 24px rgba(245, 158, 11, 0.2)',
          zIndex: 2,
          animation: 'floatReverse 5.5s ease-in-out infinite 0.5s',
        }}
      >
        <Box
          sx={{
            width: 28,
            height: 28,
            borderRadius: '8px',
            bgcolor: 'rgba(245, 158, 11, 0.18)',
            color: '#F59E0B',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <EventAvailableOutlinedIcon sx={{ fontSize: 16 }} />
        </Box>
        <Box>
          <Typography sx={{ fontSize: '0.72rem', fontWeight: 700, color: '#F8FAFC', lineHeight: 1.2 }}>
            Annual General Body
          </Typography>
          <Typography sx={{ fontSize: '0.65rem', color: '#F59E0B', fontWeight: 650 }}>
            Live Poll Active
          </Typography>
        </Box>
      </Box>
    </Box>
  );
}
